import DOMPurify from "dompurify"

import type {
  Document,
  ResponseContext,
  ResponseContextDataPoint,
  ResponseContextDataPoints,
} from "@/api/models"

import {
  createCitationPattern,
  findLibraryDocumentForCitation,
  getCitationSourceForLookup,
  resolveLibraryCitationSource,
} from "@/utils/citation-utils"

import { parseDocumentElement, parseDocumentString } from "./supporting-content-document-parser"
import { getStructuredSupportingContentItems } from "./supporting-content-structured-utils"
import { getM365TypeLabel, resolveM365CitationDisplay } from "../utils/m365-citation-display"
import { normalizePathname, normalizeTextFragmentUrl } from "../utils/url-utils"
import { resolveWebCitationDisplay } from "../utils/web-citation-display"

export type SupportingContentReferenceKind =
  | "document"
  | "web"
  | "indexed"
  | "attached"
  | "m365"
  | "file"
  | "legacy"

export type ParsedSupportingContentItem = {
  title: string
  contentHtml: string
  referenceKind: SupportingContentReferenceKind
  sourceLabel: string
  subtitle?: string
  link?: string
  citationId?: string
}

export type SupportingContentInput = string[] | ResponseContextDataPoints | ResponseContextDataPoint

function getText(el?: Element | null): string {
  if (!el) return ""
  return (el.textContent || "").trim()
}

function sanitizeContent(text: string): string {
  return DOMPurify.sanitize(text)
}

function getHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return ""
  }
}

function getTextItems(supportingContent: SupportingContentInput): string[] {
  if (Array.isArray(supportingContent)) return supportingContent
  return supportingContent.text
}

function parseWebResultElement(el: Element): ParsedSupportingContentItem | null {
  const url = el.getAttribute("url") || ""
  const titleEl = el.getElementsByTagName("title")[0]
  const contentEl = el.getElementsByTagName("content")[0]

  let title = getText(titleEl)
  if (!title) {
    title = url || "Web result"
  }

  const contentText = getText(contentEl)
  const hostname = url ? getHostname(url) : ""
  return {
    title,
    contentHtml: sanitizeContent(contentText),
    link: url || undefined,
    referenceKind: "web",
    sourceLabel: hostname || "Web",
  }
}

function parseIndexedFileElement(el: Element): ParsedSupportingContentItem | null {
  const name = el.getAttribute("name") || "Indexed file"
  const contentEl = el.getElementsByTagName("content")[0]
  const contentText = getText(contentEl)
  return {
    title: name,
    contentHtml: sanitizeContent(contentText),
    referenceKind: "indexed",
    sourceLabel: "Indexed file",
  }
}

function parseAttachedFileElement(el: Element): ParsedSupportingContentItem | null {
  const name = el.getAttribute("name") || "Attached file"
  const contentEl = el.getElementsByTagName("content")[0]
  const contentText = getText(contentEl)
  return {
    title: name,
    contentHtml: sanitizeContent(contentText),
    referenceKind: "attached",
    sourceLabel: "Attached file",
  }
}

function getTagText(source: string, tagName: string): string {
  const regex = new RegExp(`<${tagName}[^>]*>([\\s\\S]*?)</${tagName}>`, "i")
  const match = source.match(regex)
  return match ? match[1].trim() : ""
}

function parseWebResultString(source: string): ParsedSupportingContentItem | null {
  const urlMatch = source.match(/<web_result[^>]*\burl\s*=\s*"([^"]*)"/i)
  const url = urlMatch ? urlMatch[1] : ""
  const title = getTagText(source, "title") || url || "Web result"
  const content = getTagText(source, "content")
  const hostname = url ? getHostname(url) : ""
  return {
    title,
    contentHtml: sanitizeContent(content),
    link: url || undefined,
    referenceKind: "web",
    sourceLabel: hostname || "Web",
  }
}

function parseXmlItem(item: string): ParsedSupportingContentItem[] {
  const trimmed = (item || "").trim()
  if (!trimmed.startsWith("<")) return []

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(trimmed, "application/xml")
    if (doc.getElementsByTagName("parsererror").length > 0) {
      if (trimmed.toLowerCase().includes("<web_sources")) {
        const itemRegex = /<web_result[\s\S]*?<\/web_result>/gi
        const segments = trimmed.match(itemRegex) || []
        return segments
          .map((segment) => parseWebResultString(segment))
          .filter((value): value is ParsedSupportingContentItem => !!value)
      }
      if (trimmed.toLowerCase().startsWith("<web_result")) {
        const parsed = parseWebResultString(trimmed)
        return parsed ? [parsed] : []
      }
      if (trimmed.toLowerCase().includes("<document")) {
        const itemRegex = /<document[\s\S]*?<\/document>/gi
        const segments = trimmed.match(itemRegex) || [trimmed]
        return segments
          .map((segment) => parseDocumentString(segment))
          .filter((value): value is ParsedSupportingContentItem => !!value)
      }
      return []
    }

    const root = doc.documentElement
    const tag = root.tagName

    if (tag === "web_sources") {
      const results = Array.from(root.getElementsByTagName("web_result"))
      return results
        .map((el) => parseWebResultElement(el))
        .filter((value): value is ParsedSupportingContentItem => !!value)
    }

    if (tag === "web_result") {
      const parsed = parseWebResultElement(root)
      return parsed ? [parsed] : []
    }

    if (tag === "document") {
      const parsed = parseDocumentElement(root)
      return parsed ? [parsed] : []
    }

    if (tag === "indexed_file") {
      const parsed = parseIndexedFileElement(root)
      return parsed ? [parsed] : []
    }

    if (tag === "attached_file") {
      const parsed = parseAttachedFileElement(root)
      return parsed ? [parsed] : []
    }

    return []
  } catch {
    if (trimmed.toLowerCase().startsWith("<web_result")) {
      const fallback = parseWebResultString(trimmed)
      return fallback ? [fallback] : []
    }
    if (trimmed.toLowerCase().includes("<document")) {
      const itemRegex = /<document[\s\S]*?<\/document>/gi
      const segments = trimmed.match(itemRegex) || [trimmed]
      return segments
        .map((segment) => parseDocumentString(segment))
        .filter((value): value is ParsedSupportingContentItem => !!value)
    }
    return []
  }
}

function parseLegacyItem(item: string): ParsedSupportingContentItem {
  const parts = item.split(": ")
  const title = parts[0]
  const content = parts.length > 1 ? parts.slice(1).join(": ") : ""
  return {
    title,
    contentHtml: sanitizeContent(content),
    referenceKind: "legacy",
    sourceLabel: "Reference",
  }
}

export function parseSupportingContentItem(item: string): ParsedSupportingContentItem[] {
  const xmlItems = parseXmlItem(item)
  if (xmlItems.length > 0) return xmlItems
  return [parseLegacyItem(item)]
}

export function parseSupportingContentItems(
  supportingContent: SupportingContentInput
): ParsedSupportingContentItem[] {
  return [
    ...getTextItems(supportingContent).flatMap((item) => parseSupportingContentItem(item)),
    ...getStructuredSupportingContentItems(
      Array.isArray(supportingContent) ? {} : supportingContent
    ).flatMap((item) => parseSupportingContentItem(item)),
  ]
}

function getDedupKey(item: ParsedSupportingContentItem): string {
  return item.citationId || item.link || `${item.referenceKind}:${item.title}`
}

function urlsMatchForReferenceDedup(first: string | undefined, second: string): boolean {
  if (!first || !second) return false
  if (first === second) return true

  if (!first.startsWith("http://") && !first.startsWith("https://")) return false
  if (!second.startsWith("http://") && !second.startsWith("https://")) return false

  try {
    const left = new URL(normalizeTextFragmentUrl(first))
    const right = new URL(normalizeTextFragmentUrl(second))
    return (
      left.origin === right.origin &&
      normalizePathname(left.pathname) === normalizePathname(right.pathname) &&
      left.search === right.search &&
      left.hash === right.hash
    )
  } catch {
    return false
  }
}

function areDuplicateReferences(
  first: ParsedSupportingContentItem,
  second: ParsedSupportingContentItem
): boolean {
  if (first.citationId && second.citationId) {
    return (
      getCitationSourceForLookup(first.citationId) === getCitationSourceForLookup(second.citationId)
    )
  }
  if (first.link && second.link && urlsMatchForReferenceDedup(first.link, second.link)) {
    return true
  }
  return getDedupKey(first) === getDedupKey(second)
}

function mergeReference(
  existing: ParsedSupportingContentItem,
  incoming: ParsedSupportingContentItem
): ParsedSupportingContentItem {
  return {
    ...existing,
    contentHtml: existing.contentHtml || incoming.contentHtml,
    link: existing.link || incoming.link,
    citationId: existing.citationId || incoming.citationId,
    subtitle: existing.subtitle || incoming.subtitle,
  }
}

export function dedupeSupportingContentItems(
  items: ParsedSupportingContentItem[]
): ParsedSupportingContentItem[] {
  const deduped: ParsedSupportingContentItem[] = []

  for (const item of items) {
    const existingIndex = deduped.findIndex((existing) => areDuplicateReferences(existing, item))
    if (existingIndex !== -1) {
      deduped[existingIndex] = mergeReference(deduped[existingIndex], item)
      continue
    }
    deduped.push(item)
  }

  return deduped
}

export function getSupportingContentReferenceItems(
  dataPoints: ResponseContext["data_points"] | null | undefined
): ParsedSupportingContentItem[] {
  if (!dataPoints) return []

  if (Array.isArray(dataPoints) && dataPoints.every((item) => typeof item === "string")) {
    return dedupeSupportingContentItems(parseSupportingContentItems(dataPoints))
  }

  if (Array.isArray(dataPoints)) {
    const parsed = dataPoints.flatMap((dataPoint) => {
      if (typeof dataPoint === "string") {
        return parseSupportingContentItem(dataPoint)
      }
      return parseSupportingContentItems(dataPoint)
    })
    return dedupeSupportingContentItems(parsed)
  }

  return dedupeSupportingContentItems(parseSupportingContentItems(dataPoints))
}

function getTitleFromCitationSource(source: string): string {
  if (!source.includes(":")) return source
  return source.split(":").slice(1).join(":") || source
}

function getInlineCitationReferenceItem(
  citation: string,
  documents: [string, Document][] = [],
  options: { getM365SourceLabel?: Parameters<typeof getM365TypeLabel>[1] } = {}
): ParsedSupportingContentItem | null {
  const source = resolveLibraryCitationSource(citation, documents)
  if (!source) return null

  if (source.startsWith("http://") || source.startsWith("https://")) {
    const { cleanUrl, hostname, normalizedUrl } = resolveWebCitationDisplay(source)
    return {
      title: hostname || cleanUrl,
      contentHtml: "",
      link: normalizedUrl,
      referenceKind: "web",
      sourceLabel: hostname || "Web",
      subtitle: cleanUrl,
    }
  }

  if (source.startsWith("m365:")) {
    const display = resolveM365CitationDisplay(source, documents)
    return {
      title: display.title,
      contentHtml: "",
      citationId: source,
      link: display.url || undefined,
      referenceKind: "m365",
      sourceLabel: getM365TypeLabel(display.type, options.getM365SourceLabel),
      subtitle: display.metadata || undefined,
    }
  }

  if (source.startsWith("doc:")) {
    return {
      title: getTitleFromCitationSource(source),
      contentHtml: "",
      citationId: source,
      referenceKind: "document",
      sourceLabel: "Document",
    }
  }

  if (source.startsWith("localdocs:") || !source.includes(":")) {
    return {
      title: getTitleFromCitationSource(source),
      contentHtml: "",
      citationId: source,
      referenceKind: "file",
      sourceLabel: "File",
    }
  }

  const [libraryId] = source.split(":")
  const matchingDocument = findLibraryDocumentForCitation(source, documents)
  return {
    title: matchingDocument?.original_sourcefile?.trim() || getTitleFromCitationSource(source),
    subtitle: libraryId,
    contentHtml: "",
    citationId: source,
    referenceKind: "document",
    sourceLabel: "Citation",
  }
}

export function getInlineCitationReferenceItems(
  answerText: string | null | undefined,
  documents: [string, Document][] = [],
  options: { getM365SourceLabel?: Parameters<typeof getM365TypeLabel>[1] } = {}
): ParsedSupportingContentItem[] {
  if (!answerText) return []

  const citationPattern = createCitationPattern()
  const citations = Array.from(answerText.matchAll(citationPattern))
    .map((match) => match[1].trim())
    .filter((citation): citation is string => Boolean(citation))

  return dedupeSupportingContentItems(
    citations
      .map((citation) => getInlineCitationReferenceItem(citation, documents, options))
      .filter((item): item is ParsedSupportingContentItem => Boolean(item))
  )
}

function isPlaceholderDocumentReference(item: ParsedSupportingContentItem): boolean {
  return (
    item.referenceKind === "document" &&
    item.title === "Document" &&
    item.sourceLabel === "Document" &&
    !item.link &&
    !item.citationId &&
    !item.subtitle
  )
}

export function getAnswerReferenceItems({
  answerText,
  dataPoints,
  documents = [],
  fallbackDocuments = [],
  getM365SourceLabel,
}: {
  answerText: string | null | undefined
  dataPoints: ResponseContext["data_points"] | null | undefined
  documents?: [string, Document][]
  fallbackDocuments?: [string, Document][]
  getM365SourceLabel?: Parameters<typeof getM365TypeLabel>[1]
}): ParsedSupportingContentItem[] {
  const referenceDocuments =
    documents.length > 0 ? [...documents, ...fallbackDocuments] : fallbackDocuments

  return dedupeSupportingContentItems([
    ...getSupportingContentReferenceItems(dataPoints).filter(
      (item) => !isPlaceholderDocumentReference(item)
    ),
    ...getInlineCitationReferenceItems(answerText, referenceDocuments, { getM365SourceLabel }),
  ])
}
